import {memo, ReactElement, ReactNode} from 'react'

import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

interface HoldOutGroupWithExperimentProps {
  /**
   * The experiment id
   */
  experimentId: string
  /**
   * Whether the web2app holdout group is enabled
   */
  enabled?: boolean
  /**
   * The element that will be rendered when the user is in the holdout group
   */
  holdOutElement: ReactNode | null
  /**
   * The element that will be rendered when the user is not in the holdout group
   */
  elementA: ReactNode | null
  /**
   * The element that will be rendered when the user is not in the holdout group
   */
  elementB: ReactNode | null
}

/**
 * Component that conditionally renders elements based on the toggle,
 * and support nested toggles so we can experiment with users out of the holdout group
 */
const Web2AppHoldOutWithExperiment = ({
  experimentId,
  enabled = true,
  holdOutElement,
  elementA,
  elementB
}: HoldOutGroupWithExperimentProps) => {
  const toggle = useToggle()

  if (!enabled) return null

  return toggle(
    'h02hslwa-app-exclusive-deal-hold-out-group',
    () => toggle(experimentId, elementA, elementB),
    () => holdOutElement || null
  ) as ReactElement
}

interface Web2AppHoldOutWrapperProps {
  /**
   * Whether the web2app holdout group is enabled
   */
  enabled?: boolean
  /**
   * The element that will be rendered when the user is in the holdout group
   */
  holdOutElement: ReactNode | null
  /**
   * The element that will be rendered when the user is not in the holdout group
   */
  element: ReactNode
}

/**
 * Component that wraps the holdout group for the web2app elements
 */
export const Web2AppHoldOutWrapper = ({
  element,
  enabled = true,
  holdOutElement
}: Web2AppHoldOutWrapperProps) => {
  const toggle = useToggle()
  const isHydrated = useIsHydrated()

  // TODO: remove hydration check once experiment is able to run during rendering server-side
  if (!enabled || !isHydrated) return null

  return toggle(
    'h02hslwa-app-exclusive-deal-hold-out-group',
    () => element,
    () => holdOutElement || null
  ) as ReactElement
}

export const Web2AppHoldOutWithExperimentMemo = memo(
  Web2AppHoldOutWithExperiment
)
